import { useState, useEffect, useCallback, useRef } from "react";
import { isDesktopMusicRhythmRuntime } from "./desktopRuntime";
import {
  dispatchMusicRhythmAudioFrame,
  getMusicRhythmBroadcastTargets,
  globalRhythmWantsCapture,
  GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_CHANGED_EVENT,
  isGlobalMusicRhythmCaptureDelegatedToMain,
  MUSIC_RHYTHM_AUDIO_FRAME_EVENT,
  shouldKeyboardSubAppConsumeGlobalRhythmAudioFrames,
} from "./globalRhythmCapture";
import {
  isKeyboardSubAppMusicRhythmEnabled,
  KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_CHANGED_EVENT,
  readCurrentKeyboardSubAppRhythmDeviceId,
  readKeyboardSubAppRhythmEquipmentFromStorage,
  usesSubAppV1ExclusiveVirtualKeyboard,
} from "./keyboardSubAppMusicRhythm";
import type {
  AudioControlPayload,
  DesktopAudioVisualizerRole,
  MusicRhythmAudioDataPayload,
  RhythmMode,
} from "./audioVisualizerTypes";
import { normalizeRhythmMode } from "./rhythmModeOptions";
import { shouldKeepSyncRgbCaptureAlive } from "../syncRgb/syncRgbCaptureKeepAlive";
import {
  isRhythmNoiseOnly,
  resetRhythmPlaybackGate,
  RHYTHM_FRAME_STALE_MS,
} from "./rhythmPlaybackGate";
import { resolveEffectiveRhythmAmplitude } from "./deriveRhythmAmplitude";
import {
  markRhythmNativeCaptureStarted,
  readRhythmLastStartCaptureSentAt,
  readRhythmNativeCaptureActive,
  resetRhythmNativeCaptureTracking,
  writeRhythmLastStartCaptureSentAt,
} from "./rhythmNativeCaptureTracking";

export type {
  AudioControlPayload,
  DesktopAudioVisualizerRole,
  MusicRhythmAudioDataPayload,
  RhythmMode,
} from "./audioVisualizerTypes";

/** 桌面宿主 executeJavaScript 兜底通道（与 eweadn-hub-desktop WebViewRenderer 约定） */
export const EHUB_DESKTOP_AUDIO_FRAME_EVENT = "ehub-desktop-audio-frame";

type LocalElectronAudioAPI = {
  sendAudioControl?: (payload: AudioControlPayload) => void;
  /** 返回取消订阅函数（desktop 新实现）；旧实现可能无返回值 */
  onAudioDataUpdate?: (
    cb: (data: MusicRhythmAudioDataPayload) => void
  ) => void | (() => void);
};

export type UseDesktopAudioVisualizerOptions = {
  role: DesktopAudioVisualizerRole;
  captureStateKey: string;
  defaultRhythmMode?: RhythmMode;
  /** 主应用：首页恢复期间勿 start */
  shouldBlockStartCapture?: () => boolean;
};

export type UseDesktopAudioVisualizerReturn = {
  audioData: MusicRhythmAudioDataPayload | null;
  isCapturingAudioUI: boolean;
  captureCommandActive: boolean;
  hasDesktopAudioData: boolean;
  lastDesktopAudioDataAt: number;
  startAudioCapture: (opts?: { force?: boolean }) => void;
  stopAudioCapture: () => void;
  setRhythmMode: (mode: RhythmMode) => void;
};

const readCaptureState = (key: string): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeCaptureState = (key: string, active: boolean) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, active ? "1" : "0");
  } catch {
    // ignore
  }
};

function sendAudioControl(payload: AudioControlPayload) {
  const api = resolveElectronAudioAPIForRhythm();
  if (api?.sendAudioControl) {
    api.sendAudioControl(payload);
  } else {
    console.warn(
      "⚠️ [useDesktopAudioVisualizer] electronAudioAPI.sendAudioControl 不可用。"
    );
  }
}

function resolveElectronAudioAPIForRhythm(): LocalElectronAudioAPI | undefined {
  const tryWin = (
    candidate: Window | null | undefined
  ): LocalElectronAudioAPI | undefined => {
    try {
      return (
        candidate as unknown as { electronAudioAPI?: LocalElectronAudioAPI }
      )?.electronAudioAPI;
    } catch {
      return undefined;
    }
  };
  const w = window as Window & { rawWindow?: Window };
  const candidates: Array<Window | null | undefined> = [window, w.rawWindow];
  try {
    candidates.push(window.top);
  } catch {
    // ignore
  }
  try {
    candidates.push(window.parent);
  } catch {
    // ignore
  }
  for (const candidate of candidates) {
    const api = tryWin(candidate);
    if (api?.onAudioDataUpdate) return api;
  }
  return tryWin(window);
}

let lastStartCaptureSentAt = 0;
const START_CAPTURE_DEBOUNCE_MS = 600;

function readNativeCaptureActive(): boolean {
  return readRhythmNativeCaptureActive();
}

function sendStartCaptureDebounced(force = false): void {
  if (!force && readNativeCaptureActive()) return;
  const now = Date.now();
  let lastSent = lastStartCaptureSentAt;
  try {
    lastSent = Math.max(lastSent, readRhythmLastStartCaptureSentAt());
  } catch {
    // ignore
  }
  if (now - lastSent < START_CAPTURE_DEBOUNCE_MS) {
    return;
  }
  lastStartCaptureSentAt = now;
  writeRhythmLastStartCaptureSentAt(now);
  markRhythmNativeCaptureStarted();
  sendAudioControl({ type: "START_CAPTURE" });
}

function markNativeCaptureStopped(): void {
  lastStartCaptureSentAt = 0;
  resetRhythmNativeCaptureTracking();
}

export { resetRhythmNativeCaptureTracking };

/** X75 Pro / M98 / DK108 等 2.4G V1：不消费全局广播帧，必须自采音 */
function isSubAppV1ExclusiveRhythmDevice(): boolean {
  return usesSubAppV1ExclusiveVirtualKeyboard(
    readKeyboardSubAppRhythmEquipmentFromStorage()
  );
}

function shouldAcceptAudioPayload(
  role: DesktopAudioVisualizerRole,
  captureRequested: boolean
): boolean {
  if (role === "mainApp") {
    return captureRequested || globalRhythmWantsCapture();
  }
  /** V1 独占开启采音后，即使全局仍有勾选也必须收下 WASAPI 帧 */
  if (captureRequested && isSubAppV1ExclusiveRhythmDevice()) {
    return true;
  }
  if (isGlobalMusicRhythmCaptureDelegatedToMain()) {
    return (
      captureRequested || shouldKeyboardSubAppConsumeGlobalRhythmAudioFrames()
    );
  }
  return (
    captureRequested || shouldKeyboardSubAppConsumeGlobalRhythmAudioFrames()
  );
}

function shouldRegisterDesktopIpc(
  role: DesktopAudioVisualizerRole,
  captureRequested: boolean
): boolean {
  if (role === "mainApp") {
    return globalRhythmWantsCapture() || captureRequested;
  }
  /**
   * V1 独占：吃不到 MUSIC_RHYTHM_AUDIO_FRAME_EVENT，开启后必须自注册 WASAPI。
   * 旧逻辑在 globalRhythmWantsCapture() 时直接拒绝注册，导致频谱恒空、Bridge 零帧跳过。
   */
  if (captureRequested && isSubAppV1ExclusiveRhythmDevice()) {
    return true;
  }
  /** 图二全局律动：仅主应用注册 WASAPI；子应用只收 MUSIC_RHYTHM_AUDIO_FRAME_EVENT */
  if (isGlobalMusicRhythmCaptureDelegatedToMain()) {
    return false;
  }
  if (captureRequested && !globalRhythmWantsCapture()) {
    return true;
  }
  return false;
}

function shouldSubAppSendStartCapture(): boolean {
  /** V1 独占：勿被「全局委托主应用」挡住 START_CAPTURE（LS 开关写入可能略滞后） */
  if (isSubAppV1ExclusiveRhythmDevice()) {
    return true;
  }
  return !isGlobalMusicRhythmCaptureDelegatedToMain();
}

export function useDesktopAudioVisualizer(
  options: UseDesktopAudioVisualizerOptions
): UseDesktopAudioVisualizerReturn {
  const {
    role,
    captureStateKey,
    defaultRhythmMode = "classic",
    shouldBlockStartCapture,
  } = options;

  const [audioData, setAudioData] =
    useState<MusicRhythmAudioDataPayload | null>(null);
  const [isCapturingAudioUI, setIsCapturingAudioUI] = useState(false);
  const [captureCommandActive, setCaptureCommandActive] = useState(() =>
    readCaptureState(captureStateKey)
  );
  const [hasDesktopAudioData, setHasDesktopAudioData] = useState(false);
  const [lastDesktopAudioDataAt, setLastDesktopAudioDataAt] = useState(0);

  const captureRequestedRef = useRef(
    role === "mainApp"
      ? readCaptureState(captureStateKey) || globalRhythmWantsCapture()
      : false
  );
  const rhythmModeRef = useRef<RhythmMode>(
    normalizeRhythmMode(defaultRhythmMode)
  );
  const desktopFrameCountRef = useRef(0);

  const startAudioCapture = useCallback(
    (opts?: { force?: boolean }) => {
      if (shouldBlockStartCapture?.()) return;
      captureRequestedRef.current = true;
      setIsCapturingAudioUI(true);
      setCaptureCommandActive(true);
      writeCaptureState(captureStateKey, true);
      if (role === "keyboardSubApp" && !shouldSubAppSendStartCapture()) {
        return;
      }
      sendStartCaptureDebounced(Boolean(opts?.force));
    },
    [captureStateKey, shouldBlockStartCapture, role]
  );

  const stopAudioCapture = useCallback(() => {
    if (shouldKeepSyncRgbCaptureAlive()) return;
    if (role === "mainApp" && globalRhythmWantsCapture()) {
      return;
    }
    if (
      role === "keyboardSubApp" &&
      (globalRhythmWantsCapture() ||
        isGlobalMusicRhythmCaptureDelegatedToMain())
    ) {
      return;
    }
    captureRequestedRef.current = false;
    setIsCapturingAudioUI(false);
    setCaptureCommandActive(false);
    writeCaptureState(captureStateKey, false);
    setAudioData(null);
    setHasDesktopAudioData(false);
    resetRhythmPlaybackGate();

    markNativeCaptureStopped();
    sendAudioControl({ type: "STOP_CAPTURE" });
  }, [captureStateKey, role]);

  const setRhythmMode = useCallback(
    (mode: RhythmMode) => {
      const normalized = normalizeRhythmMode(mode);
      rhythmModeRef.current = normalized;
      if (role === "keyboardSubApp" && globalRhythmWantsCapture()) {
        setAudioData((prev) =>
          prev
            ? { ...prev, currentMode: normalized }
            : {
                frequencyData: [],
                currentAmplitude: 0,
                currentMode: normalized,
                isCapturingAudio: true,
              }
        );
        return;
      }
      setAudioData((prev) =>
        prev
          ? { ...prev, currentMode: normalized }
          : {
              frequencyData: [],
              currentAmplitude: 0,
              currentMode: normalized,
              isCapturingAudio: true,
            }
      );
      sendAudioControl({ type: "SET_MODE", mode: normalized });
    },
    [role]
  );

  const applyDesktopAudioPayload = useCallback(
    (data: MusicRhythmAudioDataPayload) => {
      if (!shouldAcceptAudioPayload(role, captureRequestedRef.current)) {
        return;
      }

      const globalWants = globalRhythmWantsCapture();
      let capturing: boolean;
      if (role === "mainApp" && globalWants) {
        capturing = true;
        captureRequestedRef.current = true;
      } else {
        capturing =
          captureRequestedRef.current || Boolean(data.isCapturingAudio);
        if (!capturing) {
          captureRequestedRef.current = false;
        }
      }

      const hasFreq = Boolean(data.frequencyData?.length);

      setIsCapturingAudioUI(capturing);
      setCaptureCommandActive(capturing);
      writeCaptureState(captureStateKey, capturing);

      if (hasFreq) {
        const effectiveAmplitude = resolveEffectiveRhythmAmplitude(
          data.frequencyData,
          data.currentAmplitude
        );
        const activity = {
          frequencyData: data.frequencyData,
          currentAmplitude: effectiveAmplitude,
          rms: data.rms,
          rhythmSpectrum: data.rhythmSpectrum,
          timestamp: data.timestamp ?? Date.now(),
        };
        if (isRhythmNoiseOnly(activity)) {
          const silentFrame: MusicRhythmAudioDataPayload = {
            frequencyData: [],
            currentAmplitude: 0,
            rms: 0,
            bass: 0,
            mids: 0,
            treble: 0,
            beat: false,
            timestamp: Date.now(),
            currentMode: data.currentMode ?? rhythmModeRef.current,
            isCapturingAudio: capturing,
          };
          setHasDesktopAudioData(false);
          setAudioData(silentFrame);
          if (role === "mainApp" && globalWants) {
            dispatchMusicRhythmAudioFrame(silentFrame);
          }
          return;
        }
        const frame = {
          ...data,
          currentAmplitude: effectiveAmplitude,
          isCapturingAudio: true,
        };
        if (data.currentMode) {
          rhythmModeRef.current = normalizeRhythmMode(data.currentMode);
        }
        const now = Date.now();
        setHasDesktopAudioData(true);
        setLastDesktopAudioDataAt(now);
        setAudioData(frame);
        if (role === "mainApp" && globalWants) {
          dispatchMusicRhythmAudioFrame(frame);
        }
        if (role === "mainApp") {
          desktopFrameCountRef.current += 1;
          if (desktopFrameCountRef.current === 1) {
            console.log(
              "[useDesktopAudioVisualizer:mainApp] 已收到桌面端音频帧",
              `amp=${effectiveAmplitude.toFixed(3)}`,
              data.currentAmplitude !== effectiveAmplitude
                ? `(reported=${Number(data.currentAmplitude ?? 0).toFixed(3)})`
                : "",
              `bins=${frame.frequencyData.length}`
            );
          }
        }
        return;
      }

      const silentFrame: MusicRhythmAudioDataPayload = {
        frequencyData: [],
        currentAmplitude: 0,
        rms: 0,
        bass: 0,
        mids: 0,
        treble: 0,
        beat: false,
        timestamp: Date.now(),
        currentMode: data.currentMode ?? rhythmModeRef.current,
        isCapturingAudio: capturing,
      };
      setHasDesktopAudioData(false);
      setAudioData(silentFrame);
      if (role === "mainApp" && globalRhythmWantsCapture()) {
        dispatchMusicRhythmAudioFrame(silentFrame);
      }
    },
    [captureStateKey, role]
  );

  useEffect(() => {
    if (!captureCommandActive) return;
    const id = window.setInterval(() => {
      const now = Date.now();
      setAudioData((prev) => {
        if (!prev?.frequencyData?.length) return prev;
        const frameTs = prev.timestamp ?? 0;
        const stale = frameTs > 0 && now - frameTs > RHYTHM_FRAME_STALE_MS;
        const inactive = isRhythmNoiseOnly({
          frequencyData: prev.frequencyData,
          currentAmplitude: prev.currentAmplitude,
          rms: prev.rms,
          rhythmSpectrum: prev.rhythmSpectrum,
          timestamp: prev.timestamp,
        });
        if (!stale && !inactive) return prev;
        setHasDesktopAudioData(false);
        const cleared: MusicRhythmAudioDataPayload = {
          frequencyData: [],
          currentAmplitude: 0,
          rms: 0,
          bass: 0,
          mids: 0,
          treble: 0,
          beat: false,
          timestamp: now,
          currentMode: prev.currentMode ?? rhythmModeRef.current,
          isCapturingAudio: prev.isCapturingAudio,
        };
        if (role === "mainApp" && globalRhythmWantsCapture()) {
          dispatchMusicRhythmAudioFrame(cleared);
        }
        return cleared;
      });
    }, 80);
    return () => window.clearInterval(id);
  }, [captureCommandActive, role]);

  const desktopIpcUnsubscribeRef = useRef<(() => void) | null>(null);

  const registerDesktopIpc = useCallback(() => {
    const api = resolveElectronAudioAPIForRhythm();
    if (!api?.onAudioDataUpdate) return;
    if (!shouldRegisterDesktopIpc(role, captureRequestedRef.current)) return;
    try {
      desktopIpcUnsubscribeRef.current?.();
    } catch {
      // ignore
    }
    desktopIpcUnsubscribeRef.current = null;
    const unsub = api.onAudioDataUpdate(applyDesktopAudioPayload);
    if (typeof unsub === "function") {
      desktopIpcUnsubscribeRef.current = unsub;
    }
  }, [applyDesktopAudioPayload, role]);

  /**
   * startAudioCapture 会先把 captureRequested 置 true，但 mount 时 IPC 往往尚未注册。
   * 采集开关打开后必须再 register 一次，否则子应用 V1 律动收不到频谱（实体灯会被 0 高度包打黑）。
   */
  useEffect(() => {
    if (!captureCommandActive || !isDesktopMusicRhythmRuntime()) return;
    registerDesktopIpc();
  }, [captureCommandActive, registerDesktopIpc]);

  useEffect(() => {
    if (!isDesktopMusicRhythmRuntime()) return;

    registerDesktopIpc();

    const onDesktopInjectedFrame = (ev: Event) => {
      const detail = (ev as CustomEvent<MusicRhythmAudioDataPayload>).detail;
      if (!detail) return;
      applyDesktopAudioPayload(detail);
    };
    const broadcastTargets = getMusicRhythmBroadcastTargets();
    for (const target of broadcastTargets) {
      target.addEventListener(
        EHUB_DESKTOP_AUDIO_FRAME_EVENT,
        onDesktopInjectedFrame as EventListener
      );
    }

    const onGlobalFrame = (ev: Event) => {
      if (role !== "keyboardSubApp") return;
      if (!shouldAcceptAudioPayload(role, captureRequestedRef.current)) {
        return;
      }
      const detail = (ev as CustomEvent<MusicRhythmAudioDataPayload>).detail;
      if (!detail) return;
      applyDesktopAudioPayload(detail);
    };
    for (const target of broadcastTargets) {
      target.addEventListener(
        MUSIC_RHYTHM_AUDIO_FRAME_EVENT,
        onGlobalFrame as EventListener
      );
    }

    const onSelectionChanged = () => {
      if (role !== "mainApp") return;
      if (globalRhythmWantsCapture()) {
        captureRequestedRef.current = true;
        registerDesktopIpc();
        startAudioCapture();
      } else if (!captureRequestedRef.current) {
        registerDesktopIpc();
      }
    };
    window.addEventListener(
      GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_CHANGED_EVENT,
      onSelectionChanged
    );

    return () => {
      try {
        desktopIpcUnsubscribeRef.current?.();
      } catch {
        // ignore
      }
      desktopIpcUnsubscribeRef.current = null;
      for (const target of broadcastTargets) {
        target.removeEventListener(
          EHUB_DESKTOP_AUDIO_FRAME_EVENT,
          onDesktopInjectedFrame as EventListener
        );
        target.removeEventListener(
          MUSIC_RHYTHM_AUDIO_FRAME_EVENT,
          onGlobalFrame as EventListener
        );
      }
      window.removeEventListener(
        GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_CHANGED_EVENT,
        onSelectionChanged
      );
    };
  }, [applyDesktopAudioPayload, registerDesktopIpc, role, startAudioCapture]);

  useEffect(() => {
    if (role !== "keyboardSubApp" || !isDesktopMusicRhythmRuntime()) return;

    const onSubAppSwitchChanged = (ev: Event) => {
      const detail = (ev as CustomEvent<{ enabled?: boolean }>).detail;
      if (detail?.enabled) return;
      if (
        !isKeyboardSubAppMusicRhythmEnabled(
          readCurrentKeyboardSubAppRhythmDeviceId()
        )
      ) {
        captureRequestedRef.current = false;
        setIsCapturingAudioUI(false);
        setCaptureCommandActive(false);
        writeCaptureState(captureStateKey, false);
        setAudioData(null);
        setHasDesktopAudioData(false);
        if (!globalRhythmWantsCapture() && !shouldKeepSyncRgbCaptureAlive()) {
          sendAudioControl({ type: "STOP_CAPTURE" });
        }
      }
    };
    window.addEventListener(
      KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_CHANGED_EVENT,
      onSubAppSwitchChanged
    );
    return () => {
      window.removeEventListener(
        KEYBOARD_SUB_APP_MUSIC_RHYTHM_ENABLED_CHANGED_EVENT,
        onSubAppSwitchChanged
      );
    };
  }, [captureStateKey, role]);

  useEffect(() => {
    if (role !== "mainApp" || !isDesktopMusicRhythmRuntime()) return;
    if (!globalRhythmWantsCapture()) return;
    registerDesktopIpc();
  }, [registerDesktopIpc, role]);

  return {
    audioData,
    isCapturingAudioUI,
    captureCommandActive,
    hasDesktopAudioData,
    lastDesktopAudioDataAt,
    startAudioCapture,
    stopAudioCapture,
    setRhythmMode,
  };
}
